import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { twMerge } from 'tailwind-merge'
import { GoArrow } from '~/components/ui'
import { useSeasonStats } from '../api/useSeasonStats'
import { useSeasons } from '../api/useSeasons'
import { TierIcon } from './TierIcon'

/**
 * Your season tier, linking through to the full breakdown on the dashboard.
 *
 * It was bare text and an icon: no border, no hover, no arrow, so the only thing marking it as a
 * link was the cursor — which a phone does not have. Jon found the destination by accident. It now
 * wears the same affordance the race cards use, `GoArrow` on a bordered surface that lifts on
 * hover, so "this goes somewhere" is legible before you touch it.
 *
 * It sits beside the hero's countdown block, which is *not* interactive and deliberately looks
 * quieter: a dimmer border, and no arrow. The arrow is the tell, and only one of the two has it.
 */
export const SeasonTierBadge = () => {
    const { data: seasons, isLoading } = useSeasons()
    const [latestSeasonId, setLatestSeasonId] = useState<number | null>(null)

    useEffect(() => {
        if (seasons && seasons.length > 0) {
            setLatestSeasonId(seasons[0].id)
        }
    }, [seasons])

    const { data: stats, isFetching } = useSeasonStats(latestSeasonId)

    const latestSeason = seasons?.[0]

    if (isLoading || !latestSeason) return null

    const tier = stats?.tier ?? 'Unranked'

    return (
        <Link
            to='/dashboard#season-stats'
            // The visible text reads as a label, not as a destination, so the link says where it
            // goes for anyone not looking at the arrow. No "Season" prefix — the names already
            // carry one, and "Season Season 2" is what that produced.
            aria-label={`${latestSeason.name} — ${tier}. View your season stats.`}
            className={twMerge(
                // `min-h` is the 44px tap-target floor the rest of the site is measured against;
                // this was 36px.
                //
                // `max-w` because season names are free text: the badge sized to its content, so
                // "[DEMO] Season 12 - The Very Long One" ran it past the hero copy above it. The
                // ceiling is `xl` rather than `lg` because at `lg` the live name — "[DEMO] Season
                // 3 - Vanguard" — missed by 10px and clipped its own last word.
                'group flex min-h-[2.75rem] max-w-full flex-row items-center gap-3 border border-white/20',
                'bg-black/50 px-3 py-2 backdrop-blur-sm transition duration-200 lg:max-w-xl',
                'hover:border-white/50 hover:bg-white/[0.07]',
                isFetching ? 'opacity-40' : 'opacity-100'
            )}
        >
            <TierIcon tierName={tier} size={36} />

            <div className='flex min-w-0 flex-col gap-0.5 text-left'>
                <div
                    className={twMerge(
                        'truncate text-[10px] font-bold uppercase tracking-widest',
                        latestSeason.is_active ? 'text-green-400' : 'text-white/40'
                    )}
                >
                    Season: {latestSeason.name}
                </div>
                <div className='truncate text-lg font-black uppercase leading-none'>{tier}</div>
            </div>

            {/* The word is dropped below `lg`, not the arrow. A season name like "[DEMO] Season 3
                - Vanguard" already fills the badge at 375px, and between md and lg the hero row is
                sharing its width with the countdown block. The border and the arrow carry the
                affordance on their own at those sizes. */}
            <GoArrow className='ml-1 shrink-0 lg:ml-2'>
                <span className='hidden whitespace-nowrap lg:inline'>Season stats</span>
            </GoArrow>
        </Link>
    )
}
